import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PortalInvoiceCard } from '@/modules/portal/components/PortalInvoiceCard';
import { PortalPage } from '@/modules/portal/components/ui/PortalPage';
import { PortalLoadingState, portalSpinClass } from '@/modules/portal/components/ui/portal-loading';
import { PortalStagger } from '@/modules/portal/components/ui/portal-motion';
import { fetchPortalOverview } from '@/modules/portal/services/portalOverview.api';
import { fetchPortalInvoices } from '@/modules/portal/services/portalInvoices.api';
import {
  mapPortalInvoiceRecords,
  portalInvoicesHasEligibleDocuments,
  portalInvoicesHasPaymentHistory,
} from '@/modules/portal/utils/portalInvoicesHelpers';
import { Button, buttonVariants } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';

export function PortalInvoicesPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';

  const {
    data: payments,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['portal', 'invoices', 'list'],
    queryFn: fetchPortalInvoices,
  });

  const { data: overview } = useQuery({
    queryKey: ['me', 'portal-overview'],
    queryFn: fetchPortalOverview,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PortalLoadingState
        size="md"
        message={t('common.loading')}
        className="min-h-[280px] justify-center"
      />
    );
  }

  if (error) {
    return (
      <div className="portal-glass-panel mx-auto max-w-lg space-y-4 rounded-2xl border-destructive/30 p-6">
        <h2 className="text-lg font-semibold text-foreground">
          {t('portal.invoices.error.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('portal.invoices.error.message')}</p>
        <Button type="button" variant="outline" onClick={() => void refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }

  const items = payments ?? [];
  const records = mapPortalInvoiceRecords(items, overview, locale);
  const hasHistory = portalInvoicesHasPaymentHistory(items);
  const hasInvoices = portalInvoicesHasEligibleDocuments(items);

  return (
    <PortalPage
      title={t('portal.pages.invoices.title')}
      description={t('portal.invoices.description')}
      actions={
        isFetching && !isLoading ? (
          <Icons.RefreshCw
            className={cn('h-5 w-5 text-muted-foreground', portalSpinClass)}
            aria-hidden
          />
        ) : null
      }
    >
      {!hasHistory ? (
        <div className="portal-empty-state portal-glass-panel max-w-lg space-y-4 rounded-2xl p-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted/80 text-muted-foreground">
            <Icons.Tag className="h-6 w-6" aria-hidden />
          </div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('portal.invoices.empty.noHistory.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('portal.invoices.empty.noHistory.message')}
          </p>
        </div>
      ) : !hasInvoices ? (
        <div className="portal-empty-state portal-glass-panel max-w-lg space-y-4 rounded-2xl p-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted/80 text-muted-foreground">
            <Icons.Tag className="h-6 w-6" aria-hidden />
          </div>
          <h2 className="text-lg font-semibold text-foreground">
            {t('portal.invoices.empty.noInvoices.title')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('portal.invoices.empty.noInvoices.message')}
          </p>
          <Link
            to="/portal/payments"
            className={buttonVariants({ variant: 'outline', className: 'w-full sm:w-auto' })}
          >
            {t('portal.invoices.empty.noInvoices.viewPayments')}
          </Link>
        </div>
      ) : (
        <PortalStagger className="space-y-4">
          {records.map((record) => (
            <PortalInvoiceCard key={record.payment.id} record={record} locale={locale} />
          ))}
        </PortalStagger>
      )}
    </PortalPage>
  );
}
